import { useMemo } from 'react';
import { useAuth } from '../../../../features/auth/context';
import { UserLevel } from '../../../../enums/userLevel';

interface Props {
    isDealerSelected: boolean;
    isOrgSelected: boolean;
    isSiteSelected: boolean;
}

export function useAccountTypeOptions({
    isDealerSelected,
    isOrgSelected,
    isSiteSelected,
}: Props): { value: UserLevel; label: UserLevel }[] {
    const {
        isGlobalAdmin,
        isDealerAdmin,
        isDealerEmployee,
        isOrgAdmin,
        isOrgEmployee,
    } = useAuth();

    return useMemo(() => {
        // Determine which roles are available based on current user auth
        const availableLevels: UserLevel[] = [];

        if (isGlobalAdmin) availableLevels.push(UserLevel.GlobalAdmin);
        if (isGlobalAdmin || isDealerAdmin || isDealerEmployee) {
            availableLevels.push(UserLevel.DealerAdmin, UserLevel.DealerEmployee);
        }
        if (isGlobalAdmin || isDealerAdmin || isDealerEmployee || isOrgAdmin || isOrgEmployee) {
            availableLevels.push(UserLevel.OrgAdmin, UserLevel.OrgEmployee);
        }
        availableLevels.push(UserLevel.SiteAdmin, UserLevel.SiteEmployee);

        // Determine which roles are allowed based on selection context
        let allowedLevels: UserLevel[];

        if (isSiteSelected) {
            allowedLevels = [UserLevel.SiteAdmin, UserLevel.SiteEmployee];
        } else if (isOrgSelected) {
            allowedLevels = availableLevels.filter(
                l =>
                    ![
                        UserLevel.GlobalAdmin,
                        UserLevel.DealerAdmin,
                        UserLevel.DealerEmployee,
                    ].includes(l)
            );
        } else if (isDealerSelected) {
            allowedLevels = availableLevels.filter(l => l !== UserLevel.GlobalAdmin);
        } else {
            allowedLevels = availableLevels;
        }

        return allowedLevels.map(l => ({ value: l, label: l }));
    }, [
        isGlobalAdmin,
        isDealerAdmin,
        isDealerEmployee,
        isOrgAdmin,
        isOrgEmployee,
        isDealerSelected,
        isOrgSelected,
        isSiteSelected,
    ]);
}
